import { useRef } from 'react';
import { Paperclip, X, Download, Trash2, FileText, Image, Music, FileSpreadsheet, FileIcon } from 'lucide-react';
import type { Attachment } from '../types';
import { useT } from '../i18n';
import { AUDIO_ACCEPT } from '../utils/audio';

// ── Helpers ──

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function truncateFilename(name: string, max = 24): string {
  if (name.length <= max) return name;
  const ext = name.lastIndexOf('.');
  if (ext > 0 && name.length - ext <= 6) {
    const stem = name.slice(0, ext);
    const extension = name.slice(ext);
    const stemMax = max - extension.length - 1;
    return stem.slice(0, stemMax) + '\u2026' + extension;
  }
  return name.slice(0, max - 1) + '\u2026';
}

function getFileIcon(mime: string) {
  if (mime.startsWith('image/')) return <Image size={12} />;
  if (mime.startsWith('audio/')) return <Music size={12} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return <FileSpreadsheet size={12} />;
  if (mime.includes('pdf') || mime.includes('word') || mime.includes('powerpoint') || mime.includes('presentation')) return <FileText size={12} />;
  return <FileIcon size={12} />;
}

// ── FileAttachmentPicker ──

interface FileAttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
}

export function FileAttachmentPicker({ files, onChange, maxFiles = 5 }: FileAttachmentPickerProps) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const merged = [...files, ...selected].slice(0, maxFiles);
    onChange(merged);
    // Reset input so same file can be re-selected
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="attachment-picker">
      <button
        type="button"
        className="btn-icon attachment-picker-btn"
        title={`${t('attachments.attachFiles')} — ${t('docs.commentHint')}`}
        onClick={() => inputRef.current?.click()}
        disabled={files.length >= maxFiles}
      >
        <Paperclip size={14} />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleSelect}
        accept={`.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,${AUDIO_ACCEPT}`}
      />
      {files.length > 0 && (
        <div className="attachment-picker-chips">
          {files.map((file, i) => (
            <span key={i} className="attachment-chip">
              {getFileIcon(file.type)}
              <span className="attachment-chip-name">{truncateFilename(file.name)}</span>
              <span className="attachment-chip-size">{formatFileSize(file.size)}</span>
              <button className="attachment-chip-remove" onClick={() => removeFile(i)}>
                <X size={10} />
              </button>
            </span>
          ))}
          {/* [Proposition: documents] Working documents belong on the item, task or milestone. */}
          <span className="attachment-picker-hint">{t('docs.commentHint')}</span>
        </div>
      )}
    </div>
  );
}

// ── AttachmentList ──

interface AttachmentListProps {
  attachments: Attachment[];
  currentUserId: number;
  isAdmin: boolean;
  onDelete?: (id: number) => void;
}

export function AttachmentList({ attachments, currentUserId, isAdmin, onDelete }: AttachmentListProps) {
  const { t } = useT();
  if (attachments.length === 0) return null;

  return (
    <div className="attachment-list">
      {attachments.map((att) => {
        const canDelete = onDelete && (att.uploaded_by_user_id === currentUserId || isAdmin);
        return (
          <span key={att.id} className="attachment-chip attachment-chip--download">
            {getFileIcon(att.mime_type)}
            <a
              href={`/api/attachments/${att.id}/download`}
              className="attachment-chip-name"
              title={att.original_filename}
              download
            >
              {truncateFilename(att.original_filename)}
            </a>
            <span className="attachment-chip-size">{formatFileSize(att.file_size)}</span>
            {canDelete && (
              <button
                className="attachment-chip-remove"
                title={t('attachments.deleteAttachment')}
                onClick={(e) => { e.preventDefault(); onDelete!(att.id); }}
              >
                <Trash2 size={10} />
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}
